import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../../components/ui/radio-group";
import { Button } from "../../../../../components/ui/button";
import InlineFieldPicker from "../../../../../shared/ui/dropdown/InlineFieldPicker";

type Props = {
  opened: boolean;
  close: () => void;
  traitsList?: any[];
  successMetricsList?: any[];
  metricsList?: any[];
  onMetricSubmit: (metric: string, metricName: string, type: string) => void;
};

const displayTypeOptions: { value: string; label: string; hint: string }[] = [
  { value: "value", label: "Value", hint: "Shows the current field value (text, date, etc.)" },
  { value: "stat", label: "Stat Only", hint: "Shows a number with percentage change over time" },
  { value: "line", label: "Line Chart", hint: "Displays a line chart of values over time" },
  { value: "bar", label: "Bar Chart", hint: "Displays a bar chart of values over time" },
];

function getDefaultDisplayType(item: any): string {
  if (!item) return "line";
  const pinnable = item.pinnable_as || [];
  if (pinnable.includes("line")) return "line";
  if (pinnable.includes("stat")) return "stat";
  if (pinnable.includes("value")) return "value";
  return "line";
}

const NewTrendModal = ({
  opened, close, traitsList = [], successMetricsList = [], metricsList = [], onMetricSubmit,
}: Props) => {
  const [value, setValue] = useState<any>(null);
  const [name, setName] = useState("");
  const [check, setCheck] = useState("line");

  // When field selection changes, auto-set a sensible default display type
  useEffect(() => {
    if (value) {
      setCheck(getDefaultDisplayType(value));
      if (!name) {
        setName(value.display_name?.replace(" (Computed)", "") || "");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const allowedTypes = value?.pinnable_as || ["line", "bar", "stat"];

  const handleClose = () => {
    setName("");
    setValue(null);
    setCheck("line");
    close();
  };

  return (
    <Dialog open={opened} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>New pinned metric</DialogTitle>
          <DialogDescription>
            Pin any account field to the overview for quick access. Event metrics and computed traits with history can be displayed as charts.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pt-2 pr-1">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Metric Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enterprise renewals"
              className="h-8 text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Select Field <span className="text-red-500">*</span>
            </Label>
            <InlineFieldPicker
              list={traitsList}
              successMetricsList={successMetricsList}
              metricsList={metricsList}
              selected={value?.name || null}
              onSelect={setValue}
              type="Accounts"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Display Type <span className="text-red-500">*</span>
            </Label>
            <p className="text-[11px] text-muted-foreground">
              Available display types depend on the selected field.
            </p>
            <RadioGroup
              value={check}
              onValueChange={setCheck}
              className="flex flex-wrap gap-3 pt-1"
            >
              {displayTypeOptions.map((opt) => {
                const disabled = !allowedTypes.includes(opt.value);
                return (
                  <div key={opt.value} className="relative group flex items-center gap-1.5">
                    <RadioGroupItem
                      value={opt.value}
                      id={`new-trend-${opt.value}`}
                      disabled={disabled}
                    />
                    <Label
                      htmlFor={`new-trend-${opt.value}`}
                      className={`text-xs font-normal cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      {opt.label}
                    </Label>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-48 bg-gray-800 text-white text-[10px] rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20 pointer-events-none text-center">
                      {opt.hint}
                    </div>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-2">
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!name || !value?.name}
            onClick={() => {
              if (value?.name) {
                onMetricSubmit(value.name, name, check);
                setName("");
                setValue(null);
                setCheck("line");
              }
            }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Create Metric
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewTrendModal;
